/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type LucideIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { cn } from '../../lib/utils/cn.js';

import type { JSX } from 'react';

interface StatCardProps {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly value: string;
  /** Tailwind background classes for the icon chip, e.g. `bg-emerald-500`. */
  readonly accent: string;
  readonly hint?: string | undefined;
  /** Signed period-over-period change (%). Renders a coloured up/down pill. */
  readonly trend?: number | undefined;
}

const TREND_UP = 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400';
const TREND_DOWN = 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400';

/** A dashboard metric tile (Figma stat cards) — icon chip + label + big value. */
export function StatCard({
  icon: Icon,
  label,
  value,
  accent,
  hint,
  trend,
}: StatCardProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex items-start justify-between">
        <span
          className={cn('flex h-12 w-12 items-center justify-center rounded-xl text-white', accent)}
        >
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
        {trend !== undefined ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              trend >= 0 ? TREND_UP : TREND_DOWN,
            )}
          >
            {trend >= 0 ? (
              <TrendingUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <TrendingDown className="h-3 w-3" aria-hidden="true" />
            )}
            {trend >= 0 ? '+' : ''}
            {trend}%
          </span>
        ) : null}
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1 text-3xl font-bold text-slate-800 dark:text-white">{value}</p>
      {hint !== undefined && (
        <p className="mt-1 text-xs font-medium text-slate-400 dark:text-slate-500">{hint}</p>
      )}
    </div>
  );
}
